import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import useApiStable, { resolveApiUrl } from "../utils/useApiStable";
import { registerAndSubscribePush } from "../utils/pushNotifications";

export const AuthContext = createContext(null);

export const useAuth = () => {
  return useContext(AuthContext);
};

const USER_KEYS = ["token", "username", "roles", "personId", "personUuid", "tennis04Id", "name"];

const clearUserStorage = () => USER_KEYS.forEach(k => localStorage.removeItem(k));

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const api = useApiStable();
  const logoutInProgressRef = useRef(false);

  useEffect(() => {
    localStorage.removeItem("token");
    const username = localStorage.getItem("username");

    if (!username) {
      setLoading(false);
      return;
    }

    const restoreUser = () => {
      const roles = JSON.parse(localStorage.getItem("roles") || "[]");
      const personId = localStorage.getItem("personId");
      const personUuid = localStorage.getItem("personUuid");
      const tennis04Id = localStorage.getItem("tennis04Id");
      const name = localStorage.getItem("name");
      setUser({
        username,
        roles,
        personId: personId ? Number(personId) : null,
        personUuid: personUuid || null,
        tennis04Id: tennis04Id ? Number(tennis04Id) : null,
        name: name || null,
      });
    };

    // Proactively validate the session by attempting a token refresh.
    // If the access_token cookie expired (or was cleared by the browser) but
    // the refresh_token is still valid, this silently issues a new access_token.
    // If the refresh also fails (both tokens expired), localStorage is cleared
    // and the user is not restored — they will see the login page instead of
    // stale metadata with a broken session.
    const apiUrl = resolveApiUrl();
    fetch(`${apiUrl}/auth/refresh`, { method: 'POST', credentials: 'include' })
      .then(response => {
        if (response.ok) {
          restoreUser();
          registerAndSubscribePush(api).catch(() => {});
        } else {
          USER_KEYS.forEach(k => localStorage.removeItem(k));
        }
      })
      .catch(() => {
        // Network error (e.g. offline) — optimistically restore user;
        // individual API calls will handle auth failures when connectivity returns.
        restoreUser();
      })
      .finally(() => setLoading(false));
  }, []);

  const logout = useCallback(() => {
    if (logoutInProgressRef.current) {
      return;
    }
    logoutInProgressRef.current = true;
    setUser(null);
    clearUserStorage();
    // Tell the backend to clear the httpOnly cookies without triggering refresh/retry loops
    fetch(`${api.baseURL}/auth/logout`, {
      method: "POST",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: "{}",
    })
      .catch(() => {})
      .finally(() => {
        logoutInProgressRef.current = false;
      });
  }, [api.baseURL]);

  // Listen for auth-invalidated events fired by useApiStable when all refresh attempts fail
  useEffect(() => {
    const handler = () => logout();
    window.addEventListener("auth-invalidated", handler);
    return () => window.removeEventListener("auth-invalidated", handler);
  }, [logout]);

  const login = async (username, password) => {
    try {
      const data = await api.post("/auth/login", { username, password });

      const nextUser = {
        username: data.username,
        roles: data.roles || [],
        personId: data.personId ?? null,
        personUuid: data.personUuid ?? null,
        tennis04Id: data.tennis04Id ?? null,
        name: data.name ?? null,
      };

      setUser(nextUser);
      registerAndSubscribePush(api).catch(() => {});
      // Store only non-sensitive user metadata; the token lives in an httpOnly cookie
      localStorage.setItem("username", data.username);
      localStorage.setItem("roles", JSON.stringify(data.roles || []));
      if (nextUser.personId != null) localStorage.setItem("personId", String(nextUser.personId));
      if (nextUser.personUuid) localStorage.setItem("personUuid", nextUser.personUuid);
      if (nextUser.tennis04Id != null) localStorage.setItem("tennis04Id", String(nextUser.tennis04Id));
      if (nextUser.name) localStorage.setItem("name", nextUser.name);

      return { success: true, error: null };
    } catch (error) {
      console.error("Login error:", error);
      const normalizedMessage = (error?.message || "").toLowerCase();
      const isInvalidCredentials =
        normalizedMessage.includes("benutzer unbekannt oder passwort falsch") ||
        normalizedMessage.includes("incorrect email/username or password") ||
        normalizedMessage.includes("bad credentials") ||
        normalizedMessage.includes("http error! status: 401");

      return {
        success: false,
        error: isInvalidCredentials
          ? "Benutzer unbekannt oder Passwort falsch"
          : error?.message || "Login fehlgeschlagen."
      };
    }
  };

  const register = async (username, password) => {
    try {
      await api.post("/auth/register", { username, password });
      return true;
    } catch (error) {
      console.error("Registration error:", error);
      return false;
    }
  };

  const value = {
    user,
    login,
    logout,
    register,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
